import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageForm, type LanguageFormPayload } from '@/shared/ui/organisms/LanguageForm';

import { useAddExamLang } from '../../../hooks/useAddExamLang';
import { useAvailableExamLangs } from '../../../hooks/useAvailableExamLangs';
import { useEditExamLang } from '../../../hooks/useEditExamLang';
import { useExamLangDetail } from '../../../hooks/useExamLangDetail';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface EditingLang {
  readonly id: number;
  readonly cultureInfo: string;
  readonly active: boolean;
}

export interface AddLanguageFormProps {
  readonly examId: number;
  readonly onSave: () => void;
  readonly onCancel: () => void;
  readonly editingLang?: EditingLang;
  /** Exibe o campo de instruções da prova. Padrão: `true`. */
  readonly showInstruction?: boolean;
}

// ─── Componente ───────────────────────────────────────────────────────────────

/**
 * Wrapper de feature do formulário de idioma da avaliação. Liga o
 * {@link LanguageForm} compartilhado aos endpoints de exam-management
 * (POST `/exam/{examId}/langs` e PUT `/exam/langs/{id}`).
 *
 * @param examId - ID da avaliação
 * @param onSave - Callback após salvar com sucesso
 * @param onCancel - Callback ao cancelar
 * @param editingLang - Quando presente, entra em modo de edição
 */
export function AddLanguageForm({
  examId,
  onSave,
  onCancel,
  editingLang,
  showInstruction = true,
}: AddLanguageFormProps) {
  const { t } = useTranslation('examManagement');
  const isEditMode = editingLang !== undefined;

  const { availableLangs, isLoading: isLoadingLangs } = useAvailableExamLangs(examId);
  const { addLang, isLoading: isAdding } = useAddExamLang(examId);
  const { editLang, isLoading: isEditing } = useEditExamLang(examId);
  const { langDetail, isLoading: isLoadingDetail } = useExamLangDetail(
    isEditMode ? editingLang.id : undefined,
  );

  const isSaving = isAdding || isEditing;

  // Remonta o formulário após salvar para limpar os campos (paridade com o reset original).
  const [formNonce, setFormNonce] = useState(0);

  const options = useMemo(
    () => availableLangs.map(lang => ({ key: lang.key ?? '', value: lang.value ?? '' })),
    [availableLangs],
  );

  const labels = useMemo(
    () => ({
      header: isEditMode ? t('languages.form.titleEdit') : t('languages.form.title'),
      subtitle: t('languages.form.subtitle'),
      cultureInfo: t('languages.form.cultureInfo'),
      cultureInfoPlaceholder: t('languages.form.cultureInfoPlaceholder'),
      instruction: t('languages.form.instruction'),
      instructionPlaceholder: t('languages.form.instructionPlaceholder'),
      instructionHint: t('languages.form.instructionHint'),
      active: t('languages.form.active'),
      activeHint: t('languages.form.activeHint'),
      save: t('actions.save'),
      cancel: t('actions.cancel'),
    }),
    [t, isEditMode],
  );

  function handleSave(payload: LanguageFormPayload) {
    const data = {
      cultureInfo: payload.cultureInfo,
      instruction: payload.instruction,
      active: payload.active,
    };

    const onSuccess = () => {
      setFormNonce(n => n + 1);
      onSave();
    };

    if (isEditMode) {
      editLang(editingLang.id, data, { onSuccess });
    } else {
      addLang({ ...data, active: true }, { onSuccess });
    }
  }

  return (
    <LanguageForm
      key={`${isEditMode ? editingLang.id : 'new'}-${formNonce}`}
      availableLangs={options}
      isEditMode={isEditMode}
      showInstruction={showInstruction}
      initialCultureInfo={isEditMode ? editingLang.cultureInfo : ''}
      initialInstruction={langDetail?.instruction ?? ''}
      initialActive={langDetail?.active ?? editingLang?.active ?? true}
      isLoadingLangs={isLoadingLangs}
      isLoadingDetail={isLoadingDetail}
      isSaving={isSaving}
      labels={labels}
      onSave={handleSave}
      onCancel={onCancel}
    />
  );
}
